import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { Dropdown } from "capitalrx-components-2.0/lib/forms";
import { Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Subtitle } from "capitalrx-components-2.0/lib/typography";

import { PageWrapper, ContentPanel, ThemedTabbedContainer } from "@/App/components";

import { useGetEligibilityMemberInformationQuery } from "./api";
import { InformationPanel } from "./components";
import { HoursBank, SkipMonths } from "./pages";
import { Timesheet } from "./pages/Timesheet";

export const EligibilityHomePage: FC = () => {
  const { t: translate } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "hours_bank";

  const { data: eligibilityMemberData, isLoading: memberDataIsLoading } = useGetEligibilityMemberInformationQuery();

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const onSelectTab = useCallback(
    (tab: string) => {
      const clientParam = searchParams.get("client");
      if (clientParam) {
        setSearchParams({ client: clientParam, tab: tab });
      } else {
        setSearchParams({ tab: tab });
      }
    },
    [searchParams],
  );

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <Stack>
            <Shelf>
              <Stack>
                <Subtitle>{translate("ELIGIBILITY.eligibility")}</Subtitle>
              </Stack>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        }
      >
        <InformationPanel eligibilityMemberData={eligibilityMemberData} isLoading={memberDataIsLoading} />

        {!isCompressed && (
          <ThemedTabbedContainer testId="eligibility-page-tab-container" selectedTab={tab} onSelectedTabChange={onSelectTab}>
            <ThemedTabbedContainer.TabPane
              keepHiddenContentInDOM
              testId="eligibility-hours-bank-tab"
              id="hours_bank"
              label={translate("ELIGIBILITY.hoursBank")}
            >
              <HoursBank eligibilityMemberData={eligibilityMemberData} isLoading={memberDataIsLoading} />
            </ThemedTabbedContainer.TabPane>
            <ThemedTabbedContainer.TabPane
              keepHiddenContentInDOM
              testId="eligibility-timesheet-tab"
              id="timesheet"
              label={translate("ELIGIBILITY.timesheet")}
            >
              <Timesheet />
            </ThemedTabbedContainer.TabPane>
            <ThemedTabbedContainer.TabPane
              keepHiddenContentInDOM
              testId="eligibility-skip-months-tab"
              id="skip_months"
              label={translate("ELIGIBILITY.skipMonths")}
            >
              <SkipMonths />
            </ThemedTabbedContainer.TabPane>
          </ThemedTabbedContainer>
        )}
        {isCompressed && (
          <Stack spacing="large">
            <Dropdown value={tab} onChange={(value) => onSelectTab(value.toString())} hideLabel testId="eligibility-dropdown">
              <Dropdown.Option value="hours_bank">{translate("ELIGIBILITY.hoursBank")}</Dropdown.Option>
            </Dropdown>
            {tab === "hours_bank" && <HoursBank eligibilityMemberData={eligibilityMemberData} isLoading={memberDataIsLoading} />}
          </Stack>
        )}
      </ContentPanel>
    </PageWrapper>
  );
};
